import { Button, ChoiceChips, Input, type Choice } from '@platform/design-system';
import { X } from 'lucide-react';
import { dayOrNull } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import { HISTORY_KINDS, type HistoryFilters, type HistoryKind } from './history.ts';
import styles from './History.module.css';

const ALL = 'all';

type KindChoice = HistoryKind | typeof ALL;

export type HistoryFilterBarProps = {
  filters: HistoryFilters;
  actorName: string | null;
  onChange: (next: Partial<HistoryFilters>) => void;
};

export const HistoryFilterBar = ({ filters, actorName, onChange }: HistoryFilterBarProps) => {
  const { t, locale } = usePageContext();
  const kindChoices: readonly Choice<KindChoice>[] = [
    { value: ALL, label: t('history.kind_all') },
    ...HISTORY_KINDS.map((kind) => ({ value: kind, label: t(`history.kind_${kind}`) })),
  ];
  return (
    <div className={styles['filters']}>
      <ChoiceChips
        legend={t('history.kindLegend')}
        hideLegend
        locale={locale}
        choices={kindChoices}
        value={filters.kind ?? ALL}
        onChange={(kind) => {
          onChange({ kind: kind === ALL ? null : kind });
        }}
      />
      <div className={styles['dates']}>
        <Input
          type="date"
          label={t('history.from')}
          value={filters.from ?? ''}
          max={filters.to ?? undefined}
          onChange={(event) => {
            onChange({ from: dayOrNull(event.target.value) });
          }}
        />
        <Input
          type="date"
          label={t('history.to')}
          value={filters.to ?? ''}
          min={filters.from ?? undefined}
          onChange={(event) => {
            onChange({ to: dayOrNull(event.target.value) });
          }}
        />
      </div>
      {filters.actor === null ? null : (
        <p className={styles['activeActor']}>
          {actorName === null ? t('history.byChosenActor') : t('history.byActor', { name: actorName })}
          <Button
            variant="ghost"
            icon={<X size={20} aria-hidden="true" />}
            onClick={() => {
              onChange({ actor: null });
            }}
          >
            {t('history.everyone')}
          </Button>
        </p>
      )}
    </div>
  );
};
